import React, {useCallback, useRef, useState} from "react"
import {RigidBody} from "@react-three/rapier"
import create from "zustand"
import {ThreeEvent} from "@react-three/fiber/dist/declarations/src/core/events";
import * as RAPIER from "@dimforge/rapier3d-compat";
import {Texture} from "three";
import {Vector3} from "@react-three/fiber";
import {useSpotifyImageStore} from "../spotify/Spotify";
import SpotifyImageMesh from "./Spotify";
import {initialBlocks} from "./assets/initblocks";


export interface BlockState {
    position: number[],
    texture: Texture,
}

export interface BlockStoreState {
    blocks: BlockState[],
    addBlock: (x: number, y: number, z: number, texture: Texture) => void,
    removeBlock: (x: number, y: number, z: number) => void,
}

export const useBlockStore = create<BlockStoreState>((set) => ({
    blocks: initialBlocks,
    addBlock: (x: number, y: number, z: number, texture: Texture) => set((state) => {
        console.log(texture)
        return (
            {blocks: [...state.blocks, {position: [x, y, z], texture: texture}]}
        );
    }),
    removeBlock: (x: number, y: number, z: number) => set((state) => {
        return (
            {blocks: state.blocks.filter((block) => block.position[0] !== x || block.position[1] !== y || block.position[2] !== z)}
        )
    }),
}))

interface BlocksProps {
    addBlock: (x: number, y: number, z: number) => void,
    removeBlock: (x: number, y: number, z: number) => void,
    playerRef: React.RefObject<RAPIER.RigidBody>,
}

export const Blocks = (props: BlocksProps) => {
    const blocks = useBlockStore((state) => state.blocks)
    const hover = useSpotifyImageStore((state) => state.hover)
    const setHover = useSpotifyImageStore((state) => state.setHover)


    return (
        <>
            {blocks.map((blockState, index) => <Block
                key={index} position={blockState.position} texture={blockState.texture} addBlock={props.addBlock}
                removeBlock={props.removeBlock} playerRef={props.playerRef}
            />)}
            <SpotifyImageMesh position={[-44.49, 9.54, 4]} scale={5} rotation={[0, Math.PI / 2, 0]}/>
        </>
    )
}

interface BlockProps {
    position: number[],
    texture: Texture,
    addBlock: (x: number, y: number, z: number) => void,
    removeBlock: (x: number, y: number, z: number) => void,
    playerRef: React.RefObject<RAPIER.RigidBody>,
}

export function Block(props: BlockProps) {
    const ref = useRef<RAPIER.RigidBody>(null);
    const [hover, setHover] = useState<number | null>(null)
    const playerRef = props.playerRef
    const onMove = useCallback((e: ThreeEvent<PointerEvent>) => {
        e.stopPropagation()
        if (e.faceIndex !== undefined) {
            const {x: px, y: py, z: pz} = playerRef.current!.translation();
            const {x, y, z} = ref.current!.translation();
            const distance = Math.sqrt((px - x) ** 2 + (py - y) ** 2 + (pz - z) ** 2)
            if (distance > 5) {
                console.log(distance)
                return
            }
            setHover(Math.floor(e.faceIndex / 2))
        }
    }, [])
    const texture = props.texture

    const onClick = useCallback((e: ThreeEvent<MouseEvent>) => {
        // if left click or right click
        if (ref.current !== undefined && ref.current !== null) {
            const isLeftClick = e.button === 0
            e.stopPropagation()
            const {x, y, z} = ref.current.translation()

            if (isLeftClick) {
                props.removeBlock(x, y, z)
            } else {
                const dir = [
                    [x + 1, y, z],
                    [x - 1, y, z],
                    [x, y + 1, z],
                    [x, y - 1, z],
                    [x, y, z + 1],
                    [x, y, z - 1],
                ]
                if (e.faceIndex !== undefined) {
                    const pos = dir[Math.floor(e.faceIndex / 2)];
                    props.addBlock(pos[0], pos[1], pos[2])
                }
            }
        }
    }, [])

    const onOut = useCallback(() => setHover(null), [])
    return (
        <RigidBody position={props.position as Vector3} type="fixed" colliders="cuboid" ref={ref}>
            <mesh receiveShadow castShadow onPointerMove={onMove} onPointerOut={onOut} onClick={onClick}>
                {[...Array(6)].map((_, index) => (
                    <meshStandardMaterial attach={`material-${index}`} key={index} map={texture}
                                          opacity={texture.name === 'glass' ? 0.7 : 1}
                                          color={hover === index ? "gray" : "white"} transparent={true}/>
                ))}
                <boxGeometry/>
            </mesh>
        </RigidBody>
    )
}
